"use client";

import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useMediaQuery from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { Filter, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

type DataFilterOption = {
  data_quantity: number;
  data_unit: string;
};

type ValidityFilterOption = {
  package_validity: number;
  package_validity_unit: string;
};

type FilterPackage = {
  data_quantity: number;
  package_validity: number;
};

function PackagesFilters({
  openFilters,
  setOpenFilters,
  handleClearFilter,
  handleFilterByData,
  handleFilterByValidity,
  isAnyFilterApplied,
  dataFilter,
  validityFilter,
  dataFilterOptions,
  validityFilterOptions,
  packages,
  iconOnlyBelowXl = false,
}: {
  openFilters: boolean;
  setOpenFilters: React.Dispatch<React.SetStateAction<boolean>>;
  handleFilterByData: (filter: string) => void;
  handleFilterByValidity: (filter: string) => void;
  handleClearFilter: () => void;
  isAnyFilterApplied: boolean;
  dataFilter: number | null;
  validityFilter: number | null;
  validityFilterOptions: ValidityFilterOption[];
  dataFilterOptions: DataFilterOption[];
  packages: FilterPackage[];
  iconOnlyBelowXl?: boolean;
}) {
  const t = useTranslations("Shared");
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const appliedCount =
    Number(dataFilter != null) + Number(validityFilter != null);

  const trigger = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(
        "relative flex h-10 items-center gap-1.5 rounded-md px-2 text-sm xs:gap-2 xs:px-3 xl:h-11",
        isAnyFilterApplied && "border-primary text-primary",
      )}
    >
      <Filter size={16} className="shrink-0 text-primary" />
      <span className={cn("hidden", iconOnlyBelowXl ? "xl:inline" : "xs:inline")}>
        {t("countryPackages.packagesFilter.title")}
      </span>
      {appliedCount > 0 && (
        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-600 text-primary-foreground">
          {appliedCount}
        </span>
      )}
    </Button>
  );

  const panel = (
    <FilterPanel
      dataFilter={dataFilter}
      validityFilter={validityFilter}
      dataFilterOptions={dataFilterOptions}
      validityFilterOptions={validityFilterOptions}
      handleFilterByData={handleFilterByData}
      handleFilterByValidity={handleFilterByValidity}
      handleClearFilter={handleClearFilter}
      isAnyFilterApplied={isAnyFilterApplied}
      showHeader={isDesktop}
      onClose={() => setOpenFilters(false)}
      packages={packages}
    />
  );

  if (isDesktop) {
    return (
      <Popover open={openFilters} onOpenChange={setOpenFilters}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[340px] rounded-xl border border-border bg-background p-4 shadow-xl ring-1 ring-black/5 dark:border-white/15 dark:bg-background dark:shadow-[0_20px_50px_rgba(0,0,0,0.7)] dark:ring-white/10"
        >
          {panel}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer
      shouldScaleBackground={false}
      open={openFilters}
      onOpenChange={setOpenFilters}
    >
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="flex flex-row items-center justify-between px-4 pb-2 text-left">
          <DrawerTitle className="text-base font-600">
            {t("countryPackages.packagesFilter.title")}
          </DrawerTitle>
          <DrawerClose asChild>
            <button
              type="button"
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-primary"
            >
              <X size={18} />
            </button>
          </DrawerClose>
        </DrawerHeader>
        <div className="max-h-[70vh] overflow-y-auto px-4 pb-6">{panel}</div>
      </DrawerContent>
    </Drawer>
  );
}

function FilterPanel({
  dataFilter,
  validityFilter,
  dataFilterOptions,
  validityFilterOptions,
  handleFilterByData,
  handleFilterByValidity,
  handleClearFilter,
  isAnyFilterApplied,
  showHeader,
  onClose,
  packages,
}: {
  dataFilter: number | null;
  validityFilter: number | null;
  dataFilterOptions: DataFilterOption[];
  validityFilterOptions: ValidityFilterOption[];
  handleFilterByData: (filter: string) => void;
  handleFilterByValidity: (filter: string) => void;
  handleClearFilter: () => void;
  isAnyFilterApplied: boolean;
  showHeader: boolean;
  onClose: () => void;
  packages: FilterPackage[];
}) {
  const t = useTranslations("Shared");

  const sortedDataOptions = useMemo(
    () =>
      [...dataFilterOptions].sort((a, b) => {
        if (a.data_quantity <= 0) return 1;
        if (b.data_quantity <= 0) return -1;
        return a.data_quantity - b.data_quantity;
      }),
    [dataFilterOptions],
  );

  const compatibleDataQuantities = useMemo(() => {
    if (validityFilter == null || dataFilter != null) return null;
    return new Set(
      packages
        .filter((pkg) => pkg.package_validity === validityFilter)
        .map((pkg) => pkg.data_quantity),
    );
  }, [packages, validityFilter, dataFilter]);

  const compatibleValidities = useMemo(() => {
    if (dataFilter == null) return null;
    return new Set(
      packages
        .filter((pkg) => pkg.data_quantity === dataFilter)
        .map((pkg) => pkg.package_validity),
    );
  }, [packages, dataFilter]);

  function getDataLabel(option: DataFilterOption) {
    return option.data_quantity <= 0
      ? t("countryPackages.packageCard.unlimited")
      : `${option.data_quantity} ${option.data_unit}`;
  }

  function getValidityLabel(option: ValidityFilterOption) {
    return `${option.package_validity} ${option.package_validity_unit}`;
  }

  const chipClassName = (selected: boolean, disabled: boolean) =>
    cn(
      "flex h-7 w-full items-center justify-center rounded-pill px-1.5 text-[11px] font-500 transition-colors sm:h-8 sm:px-2 sm:text-xs",
      selected
        ? "bg-primary text-primary-foreground shadow-sm"
        : disabled
          ? "cursor-not-allowed bg-muted text-muted-foreground opacity-40"
          : "bg-muted text-foreground hover:bg-primary/10 hover:text-primary",
    );

  return (
    <div className="flex flex-col gap-3">
      {showHeader && (
        <div className="flex items-center justify-between border-b border-border pb-2.5">
          <p className="text-base font-600">
            {t("countryPackages.packagesFilter.title")}
          </p>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
          >
            <X size={18} />
          </button>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-600 uppercase tracking-wide text-muted-foreground">
          {t("countryPackages.packagesFilter.dataSelect.title")}
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {sortedDataOptions.map((data) => {
            const selected = dataFilter === data.data_quantity;
            const disabled =
              !selected &&
              compatibleDataQuantities != null &&
              !compatibleDataQuantities.has(data.data_quantity);
            return (
              <button
                type="button"
                key={`${data.data_quantity}-${data.data_unit}`}
                aria-pressed={selected}
                disabled={disabled}
                onClick={() =>
                  handleFilterByData(
                    selected ? "" : String(data.data_quantity),
                  )
                }
                className={chipClassName(selected, disabled)}
              >
                {getDataLabel(data)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-600 uppercase tracking-wide text-muted-foreground">
          {t("countryPackages.packagesFilter.daySelect.title")}
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {validityFilterOptions.map((validity) => {
            const selected = validityFilter === validity.package_validity;
            const disabled =
              !selected &&
              compatibleValidities != null &&
              !compatibleValidities.has(validity.package_validity);
            return (
              <button
                type="button"
                key={`${validity.package_validity}-${validity.package_validity_unit}`}
                aria-pressed={selected}
                disabled={disabled}
                onClick={() =>
                  handleFilterByValidity(
                    selected ? "" : String(validity.package_validity),
                  )
                }
                className={chipClassName(selected, disabled)}
              >
                {getValidityLabel(validity)}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        disabled={!isAnyFilterApplied}
        className={cn(
          "flex h-8 w-full items-center justify-center rounded-pill text-sm font-500 transition-colors disabled:pointer-events-none",
          isAnyFilterApplied
            ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
            : "bg-muted text-muted-foreground",
        )}
        onClick={handleClearFilter}
      >
        {t("countryPackages.packagesFilter.resetFilter")}
      </button>
    </div>
  );
}

export default PackagesFilters;
